/**
 * FTPEPaymentOptions - Choix de mode de paiement pour l'adhésion FTPE
 * Options: Carte bancaire (Stripe) ou Virement bancaire
 */
import React, { useState } from 'react';
import { CreditCard, Building2, Copy, CheckCircle, Download, ArrowRight, Loader2, Info, Shield, Landmark, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { getToken } from '../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Coordonnées bancaires FTPE (BRED) — pour l'adhésion FTPE uniquement
const BANK_DETAILS = {
  beneficiaire: "FEDERATION DES TRES PETITES ENTREPRISES",
  banque: "BRED",
  iban: "FR76 1010 7004 7300 6370 6380 902",
  bic: "BREDFRPPXXX",
  agence: "BAIE MAHAULT JARRY",
  adresse: "387 RUE DE L'INDUSTRIE, Z.I DE JARRY, 97122 BAIE MAHAULT",
  domiciliation: "BRED BAIE MAHAULT JARRY - 387 RUE DE L'INDUSTRIE, Z.I DE JARRY, 97122 BAIE MAHAULT"
};

// Copier dans le presse-papiers
const copyToClipboard = (text, label) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copié !`);
};

// Composant carte d'info bancaire
const BankInfoRow = ({ label, value, copyable = true }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <div className="flex items-center gap-2">
      <span className="font-medium text-gray-900 text-right">{value}</span>
      {copyable && (
        <button
          onClick={() => copyToClipboard(value.replace(/\s/g, ''), label)}
          className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
          title="Copier"
        >
          <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
        </button>
      )}
    </div>
  </div>
);

export default function FTPEPaymentOptions({ 
  adhesionId, 
  amount = 200, 
  onPaymentSuccess: onPaymentSuccess,
  onPaymentPending,
  loading,
  setLoading
}) {
  const [paymentMethod, setPaymentMethod] = useState(null); // 'stripe' ou 'virement'
  const [virementSubmitted, setVirementSubmitted] = useState(false);
  
  const token = getToken();
  
  // Paiement Stripe
  const handleStripePayment = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/stripe-payments/ftpe/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          origin_url: window.location.origin,
          adhesion_id: adhesionId
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la création du paiement');
      }
      
      const data = await response.json();
      
      if (data.url) {
        // V35.63 — Mémoriser la page d'origine pour le retour post-Stripe.
        try {
          sessionStorage.setItem('payment_return_path', window.location.pathname + window.location.search);
        } catch {}
        window.location.href = data.url;
      }
    } catch (error) {
      toast.error(error.message || 'Erreur de paiement');
      setLoading(false);
    }
  };
  
  // Confirmer le virement (mettre l'adhésion en attente de virement)
  const handleVirementConfirm = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ftpe-adhesion/workflow/${adhesionId}/payment-virement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la confirmation');
      }
      
      setVirementSubmitted(true);
      toast.success('Votre demande de paiement par virement a été enregistrée');
      
      if (onPaymentPending) {
        onPaymentPending();
      }
    } catch (error) {
      toast.error(error.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };
  
  // Télécharger le RIB
  const downloadRIB = () => {
    window.open('https://customer-assets.emergentagent.com/job_admin-dashboard-643/artifacts/ue0fowkv_iban_OBJECTIF_SCOP_OUTREMER_00021253201.pdf', '_blank');
  };
  
  // Affichage après soumission virement
  if (virementSubmitted) {
    return (
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
            <Clock className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Virement en attente
          </h3>
          <p className="text-gray-600 mb-4">
            Votre adhésion sera validée dès réception du virement de <strong>{amount} €</strong>.
          </p>
          <div className="p-4 bg-white rounded-xl border border-blue-200 text-left">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-500" />
              Référence à indiquer
            </h4>
            <p className="text-sm text-gray-600 mb-2">
              Merci d&apos;indiquer en référence de votre virement :
            </p>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <code className="font-mono font-bold text-blue-700">FTPE-{adhesionId?.slice(-8).toUpperCase()}</code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(`FTPE-${adhesionId?.slice(-8).toUpperCase()}`, 'Référence')}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Un email de confirmation vous sera envoyé dès validation de votre paiement.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Montant à régler */}
      <div className="text-center">
        <div className="inline-block p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border-2 border-blue-300">
          <p className="text-gray-600 mb-2">Cotisation annuelle FTPE</p>
          <span className="text-4xl font-bold text-blue-600">{amount} €</span>
          <p className="text-sm text-gray-500 mt-1">TTC / an</p>
        </div>
      </div>
      
      {/* Choix du mode de paiement */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Option Carte Bancaire */}
        <button
          onClick={() => setPaymentMethod('stripe')}
          className={`p-6 rounded-xl border-2 text-left transition-all ${
            paymentMethod === 'stripe'
              ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              paymentMethod === 'stripe' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'
            }`}>
              <CreditCard className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Carte bancaire</h3>
              <p className="text-sm text-gray-500">Paiement immédiat</p>
            </div>
            {paymentMethod === 'stripe' && (
              <CheckCircle className="w-6 h-6 text-emerald-500 ml-auto" />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-xs">Visa</Badge>
            <Badge variant="outline" className="text-xs">Mastercard</Badge>
            <Badge variant="outline" className="text-xs">CB</Badge>
          </div>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
            <Shield className="w-3 h-3" />
            Sécurisé par Stripe
          </p>
        </button>
        
        {/* Option Virement */}
        <button
          onClick={() => setPaymentMethod('virement')}
          className={`p-6 rounded-xl border-2 text-left transition-all ${
            paymentMethod === 'virement'
              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              paymentMethod === 'virement' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
            }`}>
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Virement bancaire</h3>
              <p className="text-sm text-gray-500">Sous 2-3 jours ouvrés</p>
            </div>
            {paymentMethod === 'virement' && (
              <CheckCircle className="w-6 h-6 text-blue-500 ml-auto" />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-xs">SEPA</Badge>
            <Badge variant="outline" className="text-xs">France</Badge>
          </div>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
            <Landmark className="w-3 h-3" />
            BRED
          </p>
        </button>
      </div>
      
      {/* Détails selon le mode sélectionné */}
      {paymentMethod === 'stripe' && (
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-3 text-emerald-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Paiement sécurisé par carte bancaire</span>
            </div>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                Paiement immédiat et sécurisé
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                Activation instantanée de votre adhésion
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                Reçu envoyé par email
              </li>
            </ul>
            <Button
              onClick={handleStripePayment}
              disabled={loading}
              className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-5 h-5 mr-2" />
              )}
              Payer {amount} € par carte
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}
      
      {paymentMethod === 'virement' && (
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Landmark className="w-5 h-5 text-blue-600" />
              Coordonnées bancaires
            </CardTitle>
            <CardDescription>
              Effectuez un virement SEPA vers le compte suivant
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white rounded-xl border border-blue-200 p-4">
              <BankInfoRow label="Bénéficiaire" value={BANK_DETAILS.beneficiaire} />
              <BankInfoRow label="Banque" value={BANK_DETAILS.banque} copyable={false} />
              <BankInfoRow label="IBAN" value={BANK_DETAILS.iban} />
              <BankInfoRow label="BIC/SWIFT" value={BANK_DETAILS.bic} />
              <BankInfoRow label="Agence" value={BANK_DETAILS.agence} copyable={false} />
            </div>
            
            {/* Référence importante */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Référence obligatoire
              </h4>
              <p className="text-sm text-blue-700 mb-2">
                Indiquez impérativement cette référence dans le libellé du virement :
              </p>
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-300">
                <code className="font-mono font-bold text-blue-700 text-lg">
                  FTPE-{adhesionId?.slice(-8).toUpperCase() || 'XXXXXXXX'}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(`FTPE-${adhesionId?.slice(-8).toUpperCase()}`, 'Référence')}
                  className="border-blue-300 text-blue-700"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Copier
                </Button>
              </div>
            </div>
            
            {/* Boutons d'action */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={downloadRIB}
                className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger le RIB (PDF)
              </Button>
              <Button
                onClick={handleVirementConfirm}
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-5 h-5 mr-2" />
                )}
                J&apos;ai effectué le virement
              </Button>
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              Votre adhésion sera activée dès réception du virement (2-3 jours ouvrés)
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Message si aucun mode sélectionné */}
      {!paymentMethod && (
        <p className="text-center text-gray-500 text-sm">
          Sélectionnez un mode de paiement pour continuer
        </p>
      )}
    </div>
  );
}

export { BANK_DETAILS };
